import { VideoView, useVideoPlayer } from 'expo-video';
import { router } from 'expo-router';
import { Image as ExpoImage } from 'expo-image';
import {
  ChevronLeft,
  ChevronRight,
  Globe,
  History,
  Lock,
  MessageCircle,
  Pencil,
  PlayCircle,
  Share2,
  Trash2,
  Users,
  Wrench,
  SlidersHorizontal,
  X,
} from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  Dimensions,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  type GestureResponderEvent,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { IdentityHeader } from '@/src/components/IdentityHeader';
import { LoadableImage } from '@/src/components/LoadableImage';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import { discardQueuedOperation } from '@/src/features/offline/outbox-service';
import { useQueuedPostReaction } from '@/src/features/offline/use-outbox';
import {
  nativeShareDescription,
  nativeShareTitle,
  ShareOptionsModal,
} from '@/src/features/sharing/ShareOptionsModal';
import {
  openFacebookShareDialog,
  shareLinkWithDevice,
} from '@/src/features/sharing/share-link';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { copyTextToClipboard } from '@/src/utils/clipboard';
import { formatDate } from '@/src/utils/dates';
import { openUserProfile } from '@/src/utils/profile-navigation';

import { PhotoAttachment } from './PhotoAttachment';
import { EditContentModal, EditHistoryModal } from './ContentEditModals';
import {
  ProfileMentionInput,
  visibleProfileMentions,
} from './ProfileMentionInput';
import { ProfileMentionText } from './ProfileMentionText';
import { LinkPreviewCard } from './LinkPreviewCard';
import {
  applyReactionTransition,
  CompactReactionButton,
  createReactionMutationId,
  ReactionSummaryButton,
  shouldClearOptimisticReaction,
} from './ReactionControls';
import {
  createPost,
  editPost,
  emptyReactionCounts,
  feedReasonLabel,
  getBoundedFeedImageAspectRatio,
  getMediaAspectRatio,
  loadPostEditHistory,
  MAX_POST_BODY_LENGTH,
  postVisibilityLabel,
  recordPostShare,
  updatePostVisibility,
  type Reaction,
  type ReactionCounts,
  type PostMedia,
  type PostMediaItem,
  type PostLinkCard,
  type ProfileMention,
  type PublicPost,
  type PostVisibility,
} from './post-service';
import { normalizeCarouselPhotoIndex } from './post-photo-carousel';
import { useDeletePost } from './use-delete-post';
import { usePost, usePublicPostDetail } from './use-post';
import { usePostReaction, useSetPostReaction } from './use-post-like';

type OptimisticReactionState = {
  baseCounts: ReactionCounts;
  baseReaction: Reaction | null;
  mutationId: string;
  reaction: Reaction | null;
};

const VISIBILITY_OPTIONS: PostVisibility[] = ['public', 'friends', 'just_me'];
const VISIBILITY_MENU_WIDTH = 146;
type SharePostAction = 'copy' | 'facebook' | 'native' | 'reshare';
type SharePostMethod = 'clipboard' | 'facebook' | 'native' | 'reshare' | 'web';

function PostVideoPreview({
  media,
  onOpenViewer,
}: {
  media: PostMedia;
  onOpenViewer?: () => void;
}) {
  const [playing, setPlaying] = useState(false);
  const thumbnailURL = media.thumbnail?.downloadURL;
  const aspectRatio = getMediaAspectRatio(media);
  const player = useVideoPlayer(media.downloadURL, (videoPlayer) => {
    videoPlayer.loop = false;
  });

  useEffect(() => {
    if (playing) {
      player.play();
    }
  }, [player, playing]);

  function startPlayback() {
    if (onOpenViewer) {
      onOpenViewer();
      return;
    }

    setPlaying(true);
    player.play();
  }

  const previewContent = (
    <>
      {thumbnailURL ? (
        <ExpoImage
          accessibilityLabel="Post video thumbnail"
          cachePolicy="memory-disk"
          contentFit="contain"
          source={{ uri: thumbnailURL }}
          style={styles.videoPreviewImage}
        />
      ) : (
        <View style={styles.videoPreviewFallback}>
          <Text style={styles.videoPreviewTitle}>
            {media.thumbnailStatus === 'failed'
              ? 'Video preview unavailable'
              : 'Preparing video preview'}
          </Text>
          <Text style={styles.videoPreviewText}>Tap to play video.</Text>
        </View>
      )}
      <View style={styles.playOverlay}>
        <PlayCircle color={colors.surface} size={42} strokeWidth={2.2} />
      </View>
    </>
  );

  if (onOpenViewer) {
    return (
      <Pressable
        accessibilityLabel="Open post video"
        accessibilityRole="button"
        onPressIn={(event) => {
          event.stopPropagation();
        }}
        onPress={(event) => {
          event.stopPropagation();
          startPlayback();
        }}
        style={[styles.videoFrame, { aspectRatio }]}
      >
        {previewContent}
      </Pressable>
    );
  }

  return (
    <View style={[styles.videoFrame, { aspectRatio }]}>
      <VideoView
        nativeControls
        contentFit="contain"
        player={player}
        style={styles.video}
      />
      {!playing ? (
        <Pressable
          accessibilityLabel="Play post video"
          accessibilityRole="button"
          onPressIn={(event) => {
            event.stopPropagation();
          }}
          onPress={(event) => {
            event.stopPropagation();
            startPlayback();
          }}
          style={styles.videoPreviewOverlay}
        >
          {previewContent}
        </Pressable>
      ) : null}
    </View>
  );
}

function PostMediaView({
  media,
  onOpenVideo,
}: {
  media: PostMedia;
  onOpenVideo?: () => void;
}) {
  if (media.kind === 'image') {
    return (
      <PhotoAttachment
        accessibilityLabel="Post image"
        analyticsContext="post"
        contentFit="contain"
        frameStyle={[
          styles.imageFrame,
          { aspectRatio: getBoundedFeedImageAspectRatio(media) },
        ]}
        imageStyle={styles.image}
        originalURL={media.downloadURL}
        thumbnailURL={media.thumbnail?.downloadURL}
        viewerTitle="post photo"
      />
    );
  }

  return <PostVideoPreview media={media} onOpenViewer={onOpenVideo} />;
}

function PostPhotoCarousel({
  mediaItems,
  onOpenPhoto,
}: {
  mediaItems: PostMediaItem[];
  onOpenPhoto?: (index: number) => void;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [counterIndex, setCounterIndex] = useState(0);
  const [frameWidth, setFrameWidth] = useState(0);
  const currentPhotoIndexRef = useRef(0);
  const scrollRef = useRef<ScrollView | null>(null);
  const visibleItems = mediaItems.filter((item) => item.kind === 'image');
  const activeItem =
    visibleItems[Math.min(activeIndex, visibleItems.length - 1)];
  const aspectRatio = activeItem
    ? getBoundedFeedImageAspectRatio(activeItem)
    : 16 / 9;

  if (visibleItems.length === 0) {
    return null;
  }

  function scrollToIndex(nextIndex: number) {
    const normalizedIndex = normalizeCarouselPhotoIndex({
      fallbackIndex: activeIndex,
      photoCount: visibleItems.length,
      visibleIndex: nextIndex,
    });

    if (!frameWidth) {
      currentPhotoIndexRef.current = normalizedIndex;
      setActiveIndex(normalizedIndex);
      setCounterIndex(normalizedIndex);
      return;
    }

    scrollRef.current?.scrollTo({
      animated: true,
      x: normalizedIndex * frameWidth,
    });
    currentPhotoIndexRef.current = normalizedIndex;
    setActiveIndex(normalizedIndex);
    setCounterIndex(normalizedIndex);
  }

  function indexFromOffset(offsetX: number) {
    if (!frameWidth) {
      return activeIndex;
    }

    return Math.max(
      0,
      Math.min(visibleItems.length - 1, Math.round(offsetX / frameWidth)),
    );
  }

  function setCurrentPhotoIndex(nextIndex: number) {
    const normalizedIndex = normalizeCarouselPhotoIndex({
      fallbackIndex: currentPhotoIndexRef.current,
      photoCount: visibleItems.length,
      visibleIndex: nextIndex,
    });

    currentPhotoIndexRef.current = normalizedIndex;
    setCounterIndex(normalizedIndex);

    return normalizedIndex;
  }

  function openCurrentPhoto(event: GestureResponderEvent) {
    event.stopPropagation();
    onOpenPhoto?.(
      normalizeCarouselPhotoIndex({
        fallbackIndex: currentPhotoIndexRef.current,
        photoCount: visibleItems.length,
        visibleIndex: counterIndex,
      }),
    );
  }

  return (
    <View
      onLayout={(event) => setFrameWidth(event.nativeEvent.layout.width)}
      style={[styles.carouselFrame, { aspectRatio }]}
    >
      <ScrollView
        ref={scrollRef}
        horizontal
        onMomentumScrollEnd={(event) => {
          const nextIndex = indexFromOffset(event.nativeEvent.contentOffset.x);

          setActiveIndex(nextIndex);
          setCurrentPhotoIndex(nextIndex);
        }}
        onScroll={(event) => {
          setCurrentPhotoIndex(
            indexFromOffset(event.nativeEvent.contentOffset.x),
          );
        }}
        pagingEnabled
        scrollEventThrottle={16}
        showsHorizontalScrollIndicator={false}
        style={styles.carouselScroll}
      >
        {visibleItems.map((item, index) => (
          <Pressable
            key={item.id}
            accessibilityLabel={`Open post photo ${index + 1} of ${visibleItems.length}`}
            accessibilityRole="button"
            onPress={openCurrentPhoto}
            style={[
              styles.carouselSlide,
              { width: frameWidth || Dimensions.get('window').width },
            ]}
          >
            <View style={styles.carouselPhotoFrame}>
              <LoadableImage
                accessibilityLabel={`Post photo ${index + 1}`}
                cachePolicy="memory-disk"
                contentFit="contain"
                loaderColor={colors.primary}
                source={{
                  uri: item.thumbnail?.downloadURL ?? item.downloadURL,
                }}
                style={styles.image}
                transition={120}
              />
            </View>
          </Pressable>
        ))}
      </ScrollView>
      {visibleItems.length > 1 ? (
        <>
          <View style={styles.carouselCounter}>
            <Text style={styles.carouselCounterText}>
              {counterIndex + 1}/{visibleItems.length}
            </Text>
          </View>
          {activeIndex > 0 ? (
            <Pressable
              accessibilityLabel="Previous photo"
              accessibilityRole="button"
              onPress={(event) => {
                event.stopPropagation();
                scrollToIndex(activeIndex - 1);
              }}
              style={[styles.carouselArrow, styles.carouselArrowLeft]}
            >
              <ChevronLeft color={colors.surface} size={22} strokeWidth={2.6} />
            </Pressable>
          ) : null}
          {activeIndex < visibleItems.length - 1 ? (
            <Pressable
              accessibilityLabel="Next photo"
              accessibilityRole="button"
              onPress={(event) => {
                event.stopPropagation();
                scrollToIndex(activeIndex + 1);
              }}
              style={[styles.carouselArrow, styles.carouselArrowRight]}
            >
              <ChevronRight
                color={colors.surface}
                size={22}
                strokeWidth={2.6}
              />
            </Pressable>
          ) : null}
        </>
      ) : null}
    </View>
  );
}

function PostLinkCardView({ linkCard }: { linkCard: PostLinkCard }) {
  return (
    <Pressable
      accessibilityLabel={`Open ${linkCard.title}`}
      accessibilityRole="button"
      onPress={(event) => {
        event.stopPropagation();
        router.push(linkCard.urlPath);
      }}
      style={({ pressed }) => [
        styles.linkCard,
        pressed ? styles.linkCardPressed : null,
      ]}
    >
      <View style={styles.linkCardImageFrame}>
        {linkCard.imageURL ? (
          <ExpoImage
            accessibilityLabel=""
            cachePolicy="memory-disk"
            contentFit="cover"
            source={{ uri: linkCard.imageURL }}
            style={styles.linkCardImage}
          />
        ) : (
          <View style={styles.linkCardPlaceholder}>
            <Wrench color={colors.secondary} size={28} strokeWidth={2.2} />
          </View>
        )}
      </View>
      <View style={styles.linkCardText}>
        <Text style={styles.linkCardEyebrow}>Garage</Text>
        <Text numberOfLines={2} style={styles.linkCardTitle}>
          {linkCard.title}
        </Text>
        {linkCard.subtitle ? (
          <Text numberOfLines={1} style={styles.linkCardSubtitle}>
            {linkCard.subtitle}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}

function EmbeddedPostPreview({
  currentUid,
  onPhotoPress,
  onVideoPress,
  post,
}: {
  currentUid?: string;
  onPhotoPress?: (post: PublicPost, photoIndex: number) => void;
  onVideoPress?: (post: PublicPost) => void;
  post: PublicPost | null;
}) {
  if (!post) {
    return (
      <View style={styles.resharePreviewUnavailable}>
        <Text style={styles.resharePreviewUnavailableTitle}>
          Original post unavailable
        </Text>
        <Text style={styles.resharePreviewUnavailableText}>
          It may have been deleted or is no longer public.
        </Text>
      </View>
    );
  }

  const previewPost = post;
  const authorFullName = previewPost.authorFullName || 'Frienzzi user';
  const authorUsername = previewPost.authorUsername;
  const authorPhotoURL = previewPost.authorPhotoURL;
  const authorVerifiedBadge = previewPost.authorVerifiedBadge === true;
  const authorFounderBadge = previewPost.authorFounderBadge === true;
  function openEmbeddedAuthorProfile() {
    if (!currentUid) {
      router.push('/sign-in');
      return;
    }

    trackEvent('profile_open', {
      source: 'post_reshare_preview',
      target_is_current_user: previewPost.authorUid === currentUid,
    });
    openUserProfile({ currentUid, targetUid: previewPost.authorUid });
  }

  return (
    <Pressable
      accessibilityLabel="Open reshared post"
      accessibilityRole="button"
      onPress={(event) => {
        event.stopPropagation();
        router.push(`/posts/${previewPost.id}`);
      }}
      style={({ pressed }) => [
        styles.resharePreview,
        pressed ? styles.resharePreviewPressed : null,
      ]}
    >
      <IdentityHeader
        avatarSize={36}
        containerStyle={styles.resharePreviewHeader}
        currentUid={currentUid}
        density="compact"
        founderBadge={authorFounderBadge}
        fullName={authorFullName}
        metadata={[
          {
            id: 'created',
            kind: 'text',
            label: formatDate(previewPost.createdAt),
          },
        ]}
        photoURL={authorPhotoURL}
        uid={previewPost.authorUid}
        username={authorUsername}
        verifiedBadge={authorVerifiedBadge}
        onPress={() => openEmbeddedAuthorProfile()}
      />
      {previewPost.body ? (
        <ProfileMentionText
          body={previewPost.body}
          currentUid={currentUid}
          mentionStyle={styles.bodyMention}
          mentions={previewPost.profileMentions ?? []}
          source="post_reshare_preview"
          style={styles.resharePreviewBody}
        />
      ) : null}
      {previewPost.media?.kind === 'video' ? (
        <PostMediaView
          media={previewPost.media}
          onOpenVideo={
            onVideoPress ? () => onVideoPress(previewPost) : undefined
          }
        />
      ) : (previewPost.mediaItems ?? []).length > 0 ? (
        <PostPhotoCarousel
          mediaItems={previewPost.mediaItems ?? []}
          onOpenPhoto={
            onPhotoPress
              ? (photoIndex) => onPhotoPress(previewPost, photoIndex)
              : undefined
          }
        />
      ) : null}
      {previewPost.linkCard ? (
        <PostLinkCardView linkCard={previewPost.linkCard} />
      ) : null}
      {!previewPost.media &&
      !previewPost.linkCard &&
      previewPost.linkPreview ? (
        <LinkPreviewCard
          preview={previewPost.linkPreview}
          source="post_reshare_preview_link"
        />
      ) : null}
      {previewPost.reshare ? (
        <Text style={styles.reshareNestedNotice}>
          This post includes a reshare. Open it to see the full thread.
        </Text>
      ) : null}
    </Pressable>
  );
}

function ResharedPostPreview({
  currentUid,
  onPhotoPress,
  onVideoPress,
  postId,
}: {
  currentUid?: string;
  onPhotoPress?: (post: PublicPost, photoIndex: number) => void;
  onVideoPress?: (post: PublicPost) => void;
  postId: string;
}) {
  const signedInState = usePost(postId, { enabled: Boolean(currentUid) });
  const publicState = usePublicPostDetail(postId, !currentUid);
  const activePost = currentUid ? signedInState.post : publicState.post;
  const activeLoading = currentUid
    ? signedInState.loading
    : publicState.loading;
  const activeError = currentUid ? signedInState.error : publicState.error;

  if (activeLoading && !activePost) {
    return (
      <View style={styles.resharePreviewUnavailable}>
        <Text style={styles.resharePreviewUnavailableTitle}>
          Loading reshared post...
        </Text>
      </View>
    );
  }

  if (activeError && !activePost) {
    return <EmbeddedPostPreview currentUid={currentUid} post={null} />;
  }

  return (
    <EmbeddedPostPreview
      currentUid={currentUid}
      onPhotoPress={onPhotoPress}
      onVideoPress={onVideoPress}
      post={activePost}
    />
  );
}

function DeletePostModal({
  deleting,
  mode = 'synced',
  onCancel,
  onConfirm,
  visible,
}: {
  deleting: boolean;
  mode?: 'failed_upload' | 'synced';
  onCancel: () => void;
  onConfirm: () => void;
  visible: boolean;
}) {
  const isFailedUpload = mode === 'failed_upload';

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onCancel}
    >
      <Pressable style={styles.deleteModalBackdrop} onPress={onCancel}>
        <Pressable
          style={styles.deleteModal}
          onPress={(event) => event.stopPropagation()}
        >
          <Text style={styles.deleteModalTitle}>
            {isFailedUpload ? 'Remove failed upload?' : 'Delete this post?'}
          </Text>
          <Text style={styles.deleteModalText}>
            {isFailedUpload
              ? 'This removes the failed upload from this device. It will not retry.'
              : 'This removes the post, comments, reactions, and uploaded media from Frienzzi.'}
          </Text>
          <View style={styles.deleteModalActions}>
            <Button
              title="Cancel"
              variant="secondary"
              disabled={deleting}
              style={styles.deleteModalButton}
              onPress={onCancel}
            />
            <Button
              title={isFailedUpload ? 'Remove upload' : 'Delete post'}
              variant="danger"
              loading={deleting}
              style={styles.deleteModalButton}
              onPress={onConfirm}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function ResharePostComposeModal({
  body,
  currentUid,
  mentions,
  onBodyChange,
  onClose,
  onMentionsChange,
  onSubmit,
  originalPost,
  posting,
  visible,
}: {
  body: string;
  currentUid?: string;
  mentions: ProfileMention[];
  onBodyChange: (body: string) => void;
  onClose: () => void;
  onMentionsChange: (mentions: ProfileMention[]) => void;
  onSubmit: () => void;
  originalPost: PublicPost;
  posting: boolean;
  visible: boolean;
}) {
  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <Pressable
        style={styles.deleteModalBackdrop}
        disabled={posting}
        onPress={onClose}
      >
        <Pressable
          style={styles.reshareComposeModal}
          onPress={(event) => event.stopPropagation()}
        >
          <View style={styles.shareModalHeader}>
            <View style={styles.shareModalTitleBlock}>
              <Text style={styles.shareModalTitle}>Reshare on Frienzzi</Text>
              <Text style={styles.shareModalSubtitle}>
                Add your own message or reshare the post as-is.
              </Text>
            </View>
            <Pressable
              accessibilityLabel="Close reshare composer"
              accessibilityRole="button"
              disabled={posting}
              onPress={onClose}
              style={({ pressed }) => [
                styles.shareModalCloseButton,
                pressed && !posting
                  ? styles.shareModalCloseButtonPressed
                  : null,
              ]}
            >
              <X color={colors.mutedText} size={20} strokeWidth={2.5} />
            </Pressable>
          </View>
          <ScrollView
            contentContainerStyle={styles.reshareComposeScrollContent}
            keyboardShouldPersistTaps="handled"
            style={styles.reshareComposeScroll}
          >
            <ProfileMentionInput
              label="Your message"
              autoCapitalize="sentences"
              maxLength={MAX_POST_BODY_LENGTH}
              mentions={mentions}
              multiline
              numberOfLines={4}
              onChangeText={onBodyChange}
              onMentionsChange={onMentionsChange}
              placeholder="Add a thought..."
              scope={{ kind: 'post' }}
              style={styles.reshareMessageInput}
              textAlignVertical="top"
              value={body}
            />
            <Text style={styles.counter}>
              {body.length}/{MAX_POST_BODY_LENGTH}
            </Text>
            <EmbeddedPostPreview currentUid={currentUid} post={originalPost} />
          </ScrollView>
          <View style={styles.deleteModalActions}>
            <Button
              title="Cancel"
              variant="secondary"
              disabled={posting}
              style={styles.deleteModalButton}
              onPress={onClose}
            />
            <Button
              title="Reshare"
              loading={posting}
              loadingTitle="Resharing..."
              style={styles.deleteModalButton}
              onPress={onSubmit}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function VisibilityIcon({
  color,
  size = 15,
  visibility,
}: {
  color: string;
  size?: number;
  visibility: PostVisibility;
}) {
  if (visibility === 'friends') {
    return <Users color={color} size={size} strokeWidth={2.35} />;
  }

  if (visibility === 'just_me') {
    return <Lock color={color} size={size} strokeWidth={2.35} />;
  }

  return <Globe color={color} size={size} strokeWidth={2.35} />;
}

function PostVisibilityControl({
  canEdit,
  disabled,
  onSelect,
  saving,
  visibility,
}: {
  canEdit: boolean;
  disabled: boolean;
  onSelect: (visibility: PostVisibility) => void;
  saving: boolean;
  visibility: PostVisibility;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<{
    height: number;
    width: number;
    x: number;
    y: number;
  } | null>(null);
  const buttonRef = useRef<View | null>(null);
  const label = postVisibilityLabel(visibility);
  const activeColor = canEdit ? colors.secondary : colors.mutedText;
  const showTooltip = hovered && !menuOpen;

  useEffect(() => {
    if (!canEdit || disabled) {
      setMenuOpen(false);
    }
  }, [canEdit, disabled]);

  function openMenu() {
    buttonRef.current?.measureInWindow((x, y, width, height) => {
      setMenuAnchor({ height, width, x, y });
      setMenuOpen(true);
    });
  }

  const screenWidth =
    Platform.OS === 'web' && typeof window !== 'undefined'
      ? window.innerWidth
      : Dimensions.get('window').width;
  const menuLeft = menuAnchor
    ? Math.max(
        spacing.sm,
        Math.min(
          menuAnchor.x - 8,
          screenWidth - VISIBILITY_MENU_WIDTH - spacing.sm,
        ),
      )
    : spacing.lg;
  const menuTop = menuAnchor
    ? menuAnchor.y + menuAnchor.height + 6
    : spacing.xl;

  return (
    <View style={styles.visibilityControl}>
      <Pressable
        ref={buttonRef}
        accessibilityLabel={
          canEdit
            ? `Change post visibility. Current visibility: ${label}.`
            : `Post visibility: ${label}.`
        }
        accessibilityRole={canEdit ? 'button' : undefined}
        disabled={canEdit ? disabled : false}
        onHoverIn={Platform.OS === 'web' ? () => setHovered(true) : undefined}
        onHoverOut={Platform.OS === 'web' ? () => setHovered(false) : undefined}
        onPress={(event) => {
          event.stopPropagation();

          if (!canEdit || disabled) {
            return;
          }

          if (menuOpen) {
            setMenuOpen(false);
            return;
          }

          openMenu();
        }}
        style={({ pressed }) => [
          styles.visibilityButton,
          canEdit ? styles.visibilityButtonEditable : null,
          pressed && canEdit && !disabled
            ? styles.visibilityButtonPressed
            : null,
          saving ? styles.visibilityButtonSaving : null,
        ]}
      >
        <VisibilityIcon color={activeColor} visibility={visibility} />
      </Pressable>
      {showTooltip ? (
        <View pointerEvents="none" style={styles.visibilityTooltip}>
          <Text style={styles.visibilityTooltipText}>{label}</Text>
        </View>
      ) : null}
      <Modal
        animationType="fade"
        transparent
        visible={menuOpen}
        onRequestClose={() => setMenuOpen(false)}
      >
        <Pressable
          style={styles.visibilityMenuOverlay}
          onPress={() => setMenuOpen(false)}
        >
          <Pressable
            style={[styles.visibilityMenu, { left: menuLeft, top: menuTop }]}
            onPress={(event) => event.stopPropagation()}
          >
            {VISIBILITY_OPTIONS.map((option) => {
              const optionLabel = postVisibilityLabel(option);
              const selected = option === visibility;

              return (
                <Pressable
                  key={option}
                  accessibilityLabel={`Set post visibility to ${optionLabel}`}
                  accessibilityRole="button"
                  disabled={saving}
                  onPress={() => {
                    setMenuOpen(false);

                    if (!selected) {
                      onSelect(option);
                    }
                  }}
                  style={({ pressed }) => [
                    styles.visibilityMenuItem,
                    selected ? styles.visibilityMenuItemSelected : null,
                    pressed && !saving
                      ? styles.visibilityMenuItemPressed
                      : null,
                  ]}
                >
                  <VisibilityIcon
                    color={selected ? colors.primary : colors.mutedText}
                    size={16}
                    visibility={option}
                  />
                  <Text
                    style={[
                      styles.visibilityMenuItemText,
                      selected ? styles.visibilityMenuItemTextSelected : null,
                    ]}
                  >
                    {optionLabel}
                  </Text>
                </Pressable>
              );
            })}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

export function PostCard({
  openDetailOnPress,
  onDeleted,
  onCommentPress,
  onDetailOpen,
  onFeedFeedback,
  onPostEngagement,
  onPhotoPress,
  onVideoPress,
  post,
  showCommentAction = true,
  showCommentCount = showCommentAction,
}: {
  openDetailOnPress?: boolean;
  onDeleted?: () => void;
  onCommentPress?: (post: PublicPost) => void;
  onDetailOpen?: (post: PublicPost) => void;
  onFeedFeedback?: (post: PublicPost, type: 'hide' | 'less_like_this') => void;
  onPostEngagement?: (post: PublicPost) => void;
  onPhotoPress?: (post: PublicPost, photoIndex: number) => void;
  onVideoPress?: (post: PublicPost) => void;
  post: PublicPost;
  showCommentAction?: boolean;
  showCommentCount?: boolean;
}) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const hasHydratedViewerReaction = 'viewerReaction' in post;
  const reactionState = usePostReaction(
    post.id,
    user?.uid,
    !hasHydratedViewerReaction,
  );
  const queuedReaction = useQueuedPostReaction({
    postId: post.id,
    uid: user?.uid,
  });
  const setReaction = useSetPostReaction(user?.uid);
  const deletePost = useDeletePost();
  const [optimisticReaction, setOptimisticReaction] =
    useState<OptimisticReactionState | null>(null);
  const [optimisticVisibility, setOptimisticVisibility] =
    useState<PostVisibility | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [discardingFailedUpload, setDiscardingFailedUpload] = useState(false);
  const [optimisticShareCount, setOptimisticShareCount] = useState<
    number | null
  >(null);
  const [sharing, setSharing] = useState(false);
  const [shareMenuVisible, setShareMenuVisible] = useState(false);
  const [sharingAction, setSharingAction] = useState<SharePostAction | null>(
    null,
  );
  const [reshareComposerVisible, setReshareComposerVisible] = useState(false);
  const [reshareBody, setReshareBody] = useState('');
  const [reshareMentions, setReshareMentions] = useState<ProfileMention[]>([]);
  const sharingRef = useRef(false);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [showRankingInfo, setShowRankingInfo] = useState(false);
  const [editingPost, setEditingPost] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [showEditHistory, setShowEditHistory] = useState(false);
  const authorFullName = post.authorFullName || 'Frienzzi user';
  const authorUsername = post.authorUsername;
  const authorPhotoURL = post.authorPhotoURL;
  const authorVerifiedBadge = post.authorVerifiedBadge === true;
  const authorFounderBadge = post.authorFounderBadge === true;
  const hasPendingSync = Boolean(
    post.syncStatus && post.syncStatus !== 'synced',
  );
  const baseReactionCounts = post.reactionCounts ?? emptyReactionCounts();
  const persistedReaction = hasHydratedViewerReaction
    ? (post.viewerReaction ?? null)
    : reactionState.reaction;
  const optimisticCounts = optimisticReaction
    ? applyReactionTransition(optimisticReaction)
    : null;
  const queuedCounts =
    queuedReaction !== undefined && !optimisticCounts
      ? applyReactionTransition({
          baseCounts: baseReactionCounts,
          baseReaction: hasHydratedViewerReaction
            ? (post.viewerReaction ?? null)
            : reactionState.reaction,
          reaction: queuedReaction,
        })
      : null;
  const visibleReaction = optimisticReaction
    ? optimisticReaction.reaction
    : queuedReaction !== undefined
      ? queuedReaction
      : persistedReaction;
  const visibleReactionCounts =
    optimisticCounts?.counts ?? queuedCounts?.counts ?? baseReactionCounts;
  const totalVisibleReactions = Object.values(visibleReactionCounts).reduce(
    (total, count) => total + count,
    0,
  );
  const reactionDisabled = !hasHydratedViewerReaction && reactionState.loading;
  const canDeletePost = user?.uid === post.authorUid && !hasPendingSync;
  const canDiscardFailedPost =
    user?.uid === post.authorUid && post.syncStatus === 'failed';
  const canEditPost = user?.uid === post.authorUid && !hasPendingSync;
  const hasEditHistory = (post.editCount ?? 0) > 0;
  const visibleVisibility = optimisticVisibility ?? post.visibility;
  const visibleShareCount = optimisticShareCount ?? post.shareCount;
  const shareUrl = `https://frienzzi.com/posts/${post.id}`;
  const canEditVisibility = user?.uid === post.authorUid && !hasPendingSync;
  const canOpenShareMenu =
    visibleVisibility === 'public' &&
    !hasPendingSync &&
    Boolean(user?.uid) &&
    !sharing;
  const canResharePost =
    visibleVisibility === 'public' && !hasPendingSync && Boolean(user?.uid);
  const canOpenDetail =
    (openDetailOnPress ?? showCommentAction) && !hasPendingSync;

  useEffect(() => {
    if (reactionState.error) {
      showError(reactionState.error, 'Unable to load your post reaction.');
    }
  }, [reactionState.error, showError]);

  useEffect(() => {
    setOptimisticReaction(null);
    setOptimisticVisibility(null);
    setOptimisticShareCount(null);
    sharingRef.current = false;
    setSharing(false);
    setSharingAction(null);
    setShareMenuVisible(false);
    setReshareComposerVisible(false);
    setReshareBody('');
    setReshareMentions([]);
    setVisibilitySaving(false);
    setEditingPost(false);
    setEditSaving(false);
    setShowEditHistory(false);
  }, [post.id, user?.uid]);

  useEffect(() => {
    if (
      optimisticShareCount != null &&
      post.shareCount >= optimisticShareCount
    ) {
      setOptimisticShareCount(null);
    }
  }, [optimisticShareCount, post.shareCount]);

  useEffect(() => {
    if (optimisticVisibility && post.visibility === optimisticVisibility) {
      setOptimisticVisibility(null);
    }
  }, [optimisticVisibility, post.visibility]);

  useEffect(() => {
    if (
      optimisticReaction &&
      optimisticCounts &&
      shouldClearOptimisticReaction({
        currentCounts: baseReactionCounts,
        currentMutationAck: reactionState.mutationId,
        expectedCounts: optimisticCounts.counts,
        startingCounts: optimisticReaction.baseCounts,
        persistedReaction,
        queuedReaction,
        targetMutationId: optimisticReaction.mutationId,
        targetReaction: optimisticReaction.reaction,
      })
    ) {
      setOptimisticReaction(null);
    }
  }, [
    baseReactionCounts,
    optimisticCounts,
    optimisticReaction,
    persistedReaction,
    queuedReaction,
    reactionState.mutationId,
  ]);

  function openAuthorProfile() {
    if (!user?.uid) {
      router.push('/sign-in');
      return;
    }

    trackEvent('profile_open', {
      source: 'post_card',
      target_is_current_user: post.authorUid === user?.uid,
    });
    openUserProfile({ currentUid: user?.uid, targetUid: post.authorUid });
  }

  function openPostDetail() {
    onDetailOpen?.(post);
    trackEvent('post_detail_open', {
      source: 'post_card',
    });
    router.push(`/posts/${post.id}`);
  }

  function openCommentComposer() {
    if (!user?.uid) {
      router.push('/sign-in');
      return;
    }

    if (onCommentPress) {
      onCommentPress(post);
      return;
    }

    openPostDetail();
  }

  function openShareMenu() {
    if (!user?.uid) {
      router.push('/sign-in');
      return;
    }

    if (visibleVisibility !== 'public' || hasPendingSync) {
      return;
    }

    setShareMenuVisible(true);
  }

  function beginShareAction(action: SharePostAction) {
    if (
      !user?.uid ||
      sharingRef.current ||
      visibleVisibility !== 'public' ||
      hasPendingSync
    ) {
      if (!user?.uid) {
        router.push('/sign-in');
      }
      return false;
    }

    sharingRef.current = true;
    setSharing(true);
    setSharingAction(action);
    trackEvent('post_share_start', { method: action });
    return true;
  }

  function finishShareAction() {
    sharingRef.current = false;
    setSharing(false);
    setSharingAction(null);
  }

  async function recordSuccessfulShare(method: SharePostMethod) {
    try {
      const result = await recordPostShare(post.id);

      setOptimisticShareCount(result.shareCount);
      onPostEngagement?.(post);
      trackEvent('post_share_complete', { method });
      return true;
    } catch {
      trackEvent('post_share_count_failed', { method });
      return false;
    }
  }

  async function copyPostLink() {
    if (!beginShareAction('copy')) {
      return;
    }

    try {
      await copyTextToClipboard(shareUrl);
      await recordSuccessfulShare('clipboard');
      showSuccess('Post link copied.', 'Ready to share');
      setShareMenuVisible(false);
    } catch (error) {
      showError(error, 'Unable to copy that post link.');
    } finally {
      finishShareAction();
    }
  }

  async function sharePostWithDevice() {
    if (!beginShareAction('native')) {
      return;
    }

    try {
      const result = await shareLinkWithDevice({
        title: `${authorFullName} on Frienzzi`,
        text: post.body ?? 'View this post on Frienzzi.',
        url: shareUrl,
      });

      if (result.cancelled) {
        return;
      }

      await recordSuccessfulShare(result.method);
      if (result.method === 'clipboard') {
        showSuccess('Post link copied.', 'Ready to share');
      }
      setShareMenuVisible(false);
    } catch (error) {
      showError(error, 'Unable to share that post.');
    } finally {
      finishShareAction();
    }
  }

  async function sharePostToFacebook() {
    if (!beginShareAction('facebook')) {
      return;
    }

    try {
      await openFacebookShareDialog(shareUrl);
      await recordSuccessfulShare('facebook');
      showSuccess('Facebook share is ready.', 'Share opened');
      setShareMenuVisible(false);
    } catch (error) {
      showError(error, 'Unable to open Facebook sharing.');
    } finally {
      finishShareAction();
    }
  }

  function openReshareComposer() {
    if (!user?.uid) {
      router.push('/sign-in');
      return;
    }

    if (!canResharePost) {
      return;
    }

    setShareMenuVisible(false);
    setReshareBody('');
    setReshareMentions([]);
    setReshareComposerVisible(true);
  }

  async function publishResharePostOnFrienzzi() {
    if (!beginShareAction('reshare')) {
      return;
    }

    const currentUser = user;
    const trimmedBody = reshareBody.trim();
    const activeMentions = visibleProfileMentions(reshareMentions, trimmedBody);

    try {
      if (!currentUser?.uid) {
        throw new Error('Sign in is required.');
      }

      await createPost({
        authorFullName: currentUser.displayName?.trim().slice(0, 80) || null,
        authorUid: currentUser.uid,
        body: trimmedBody,
        profileMentions: activeMentions,
        reshareOfPost: post,
        visibility: 'public',
      });
      await recordSuccessfulShare('reshare');
      trackEvent('post_reshare_create');
      showSuccess('Your reshare is live.', 'Reshared on Frienzzi');
      setReshareComposerVisible(false);
      setReshareBody('');
      setReshareMentions([]);
    } catch (error) {
      showError(error, 'Unable to reshare that post.');
    } finally {
      finishShareAction();
    }
  }

  function chooseReaction(reaction: Reaction) {
    if (!user?.uid || reactionDisabled || hasPendingSync) {
      return;
    }

    const nextReaction = visibleReaction === reaction ? null : reaction;
    const mutationId = createReactionMutationId();

    setOptimisticReaction({
      baseCounts:
        optimisticCounts?.counts ?? queuedCounts?.counts ?? baseReactionCounts,
      baseReaction: visibleReaction,
      mutationId,
      reaction: nextReaction,
    });

    setReaction.mutate(
      { mutationId, post, reaction: nextReaction },
      {
        onError: (error) => {
          setOptimisticReaction(null);
          showError(error, 'Unable to update that post reaction.');
        },
        onSuccess: () => {
          onPostEngagement?.(post);
        },
      },
    );
  }

  async function confirmDeletePost() {
    if (canDiscardFailedPost) {
      setDiscardingFailedUpload(true);

      try {
        const discarded = await discardQueuedOperation(post.id);

        if (!discarded) {
          throw new Error('That failed upload is no longer queued.');
        }

        trackEvent('post_upload_discard');
        showSuccess('The failed upload was removed.', 'Upload removed');
        setConfirmingDelete(false);
        onDeleted?.();
      } catch (error) {
        showError(error, 'Unable to remove that failed upload.');
      } finally {
        setDiscardingFailedUpload(false);
      }

      return;
    }

    try {
      await deletePost.mutateAsync(post.id);
      trackEvent('post_delete');
      setConfirmingDelete(false);
      onDeleted?.();
      showSuccess('Your post was deleted.', 'Post removed');
    } catch (error) {
      showError(error, 'Unable to delete that post.');
    }
  }

  async function savePostEdit(
    nextBody: string,
    nextMentions: PublicPost['profileMentions'],
  ) {
    setEditSaving(true);

    try {
      await editPost({
        body: nextBody,
        postId: post.id,
        profileMentions: nextMentions,
      });
      trackEvent('post_edit');
      showSuccess('Your post was updated.', 'Post saved');
      setEditingPost(false);
    } catch (error) {
      showError(error, 'Unable to update that post.');
    } finally {
      setEditSaving(false);
    }
  }

  async function changePostVisibility(nextVisibility: PostVisibility) {
    if (
      !canEditVisibility ||
      visibilitySaving ||
      nextVisibility === visibleVisibility
    ) {
      return;
    }

    setOptimisticVisibility(nextVisibility);
    setVisibilitySaving(true);

    try {
      await updatePostVisibility(post.id, nextVisibility);
      trackEvent('post_visibility_update', {
        visibility: nextVisibility,
      });
      showSuccess(
        `This post is now ${postVisibilityLabel(nextVisibility).toLowerCase()}.`,
        'Visibility updated',
      );
    } catch (error) {
      setOptimisticVisibility(null);
      showError(error, 'Unable to update post visibility.');
    } finally {
      setVisibilitySaving(false);
    }
  }

  function sendFeedFeedback(type: 'hide' | 'less_like_this') {
    if (!onFeedFeedback) {
      return;
    }

    onFeedFeedback(post, type);
    showSuccess(
      type === 'hide'
        ? 'This post will be hidden from your ranked feed.'
        : 'We will show you fewer posts like this.',
      type === 'hide' ? 'Post hidden' : 'Feedback saved',
    );
  }

  const rankingReasonLabels =
    post.ranking?.reasonCodes.map(feedReasonLabel).filter(Boolean) ?? [];

  const cardContent = (
    <>
      <IdentityHeader
        avatarSize={44}
        containerStyle={styles.header}
        currentUid={user?.uid}
        founderBadge={authorFounderBadge}
        fullName={authorFullName}
        metadata={[
          {
            element: (
              <PostVisibilityControl
                canEdit={canEditVisibility}
                disabled={visibilitySaving}
                saving={visibilitySaving}
                visibility={visibleVisibility}
                onSelect={(nextVisibility) => {
                  void changePostVisibility(nextVisibility);
                }}
              />
            ),
            id: 'visibility',
            kind: 'control',
            label: postVisibilityLabel(visibleVisibility),
          },
          ...(hasEditHistory && user?.uid
            ? [
                {
                  element: (
                    <Pressable
                      accessibilityLabel="View post edit history"
                      accessibilityRole="button"
                      onPress={(event) => {
                        event.stopPropagation();
                        setShowEditHistory(true);
                      }}
                      style={({ pressed }) => [
                        styles.editedButton,
                        pressed ? styles.editedButtonPressed : null,
                      ]}
                    >
                      <History
                        color={colors.primary}
                        size={13}
                        strokeWidth={2.6}
                      />
                      <Text style={styles.editedText}>Edited</Text>
                    </Pressable>
                  ),
                  id: 'edited',
                  kind: 'control',
                  label: 'Edited',
                } as const,
              ]
            : []),
          ...(hasPendingSync
            ? [
                {
                  id: 'sync',
                  kind: 'text',
                  label:
                    post.syncStatus === 'failed'
                      ? 'Sync failed'
                      : post.syncStatus === 'uploading'
                        ? 'Uploading'
                        : 'Pending sync',
                  tone: post.syncStatus === 'failed' ? 'danger' : 'muted',
                } as const,
              ]
            : []),
        ]}
        photoURL={authorPhotoURL}
        rightSlot={
          canEditPost || canDeletePost || canDiscardFailedPost ? (
            <View style={styles.postHeaderActions}>
              {canEditPost ? (
                <Pressable
                  accessibilityLabel="Edit post"
                  accessibilityRole="button"
                  disabled={editSaving}
                  onPress={(event) => {
                    event.stopPropagation();
                    setEditingPost(true);
                  }}
                  style={({ pressed }) => [
                    styles.headerIconButton,
                    pressed && !editSaving
                      ? styles.headerIconButtonPressed
                      : null,
                    editSaving ? styles.headerIconButtonDisabled : null,
                  ]}
                >
                  <Pencil
                    color={colors.secondary}
                    size={17}
                    strokeWidth={2.4}
                  />
                </Pressable>
              ) : null}
              {canDeletePost || canDiscardFailedPost ? (
                <Pressable
                  accessibilityLabel={
                    canDiscardFailedPost
                      ? 'Remove failed upload'
                      : 'Delete post'
                  }
                  accessibilityRole="button"
                  disabled={deletePost.isPending || discardingFailedUpload}
                  onPress={(event) => {
                    event.stopPropagation();
                    setConfirmingDelete(true);
                  }}
                  style={({ pressed }) => [
                    styles.deletePostButton,
                    pressed && !deletePost.isPending && !discardingFailedUpload
                      ? styles.deletePostButtonPressed
                      : null,
                    deletePost.isPending || discardingFailedUpload
                      ? styles.deletePostButtonDisabled
                      : null,
                  ]}
                >
                  <Trash2 color={colors.danger} size={18} strokeWidth={2.4} />
                </Pressable>
              ) : null}
            </View>
          ) : null
        }
        timestamp={post.createdAt}
        uid={post.authorUid}
        username={authorUsername}
        verifiedBadge={authorVerifiedBadge}
        onPress={() => openAuthorProfile()}
      />
      {post.body ? (
        canOpenDetail ? (
          <Pressable
            accessibilityLabel="View post details"
            accessibilityRole="button"
            onPress={(event) => {
              event.stopPropagation();
              openPostDetail();
            }}
            style={({ pressed }) => [
              styles.bodyButton,
              pressed ? styles.bodyButtonPressed : null,
            ]}
          >
            <ProfileMentionText
              body={post.body}
              currentUid={user?.uid}
              mentionStyle={styles.bodyMention}
              mentions={post.profileMentions ?? []}
              source="post_mention"
              style={styles.body}
            />
          </Pressable>
        ) : (
          <ProfileMentionText
            body={post.body}
            currentUid={user?.uid}
            mentionStyle={styles.bodyMention}
            mentions={post.profileMentions ?? []}
            source="post_mention"
            style={styles.body}
          />
        )
      ) : null}
      {post.media?.kind === 'video' ? (
        <PostMediaView
          media={post.media}
          onOpenVideo={onVideoPress ? () => onVideoPress(post) : undefined}
        />
      ) : (post.mediaItems ?? []).length > 0 ? (
        <PostPhotoCarousel
          mediaItems={post.mediaItems ?? []}
          onOpenPhoto={
            onPhotoPress
              ? (photoIndex) => onPhotoPress(post, photoIndex)
              : undefined
          }
        />
      ) : null}
      {post.linkCard ? <PostLinkCardView linkCard={post.linkCard} /> : null}
      {!post.media && !post.linkCard && post.linkPreview ? (
        <LinkPreviewCard
          preview={post.linkPreview}
          source="post_link_preview"
        />
      ) : null}
      {post.reshare ? (
        <ResharedPostPreview
          currentUid={user?.uid}
          onPhotoPress={onPhotoPress}
          onVideoPress={onVideoPress}
          postId={post.reshare.postId}
        />
      ) : null}
      {post.ranking ? (
        <View style={styles.rankingPanel}>
          <Pressable
            accessibilityLabel="Why am I seeing this post?"
            accessibilityRole="button"
            onPress={(event) => {
              event.stopPropagation();
              setShowRankingInfo(true);
            }}
            style={({ pressed }) => [
              styles.rankingReasonButton,
              pressed ? styles.rankingReasonButtonPressed : null,
            ]}
          >
            <SlidersHorizontal
              color={colors.secondary}
              size={15}
              strokeWidth={2.6}
            />
            <Text numberOfLines={1} style={styles.rankingReasonText}>
              {rankingReasonLabels[0] ?? 'Ranked for you'}
            </Text>
          </Pressable>
          {onFeedFeedback ? (
            <View style={styles.rankingActions}>
              <Pressable
                accessibilityLabel="Show me less like this"
                accessibilityRole="button"
                onPress={(event) => {
                  event.stopPropagation();
                  sendFeedFeedback('less_like_this');
                }}
                style={({ pressed }) => [
                  styles.rankingActionButton,
                  pressed ? styles.rankingActionButtonPressed : null,
                ]}
              >
                <Text style={styles.rankingActionText}>Less</Text>
              </Pressable>
              <Pressable
                accessibilityLabel="Hide this post"
                accessibilityRole="button"
                onPress={(event) => {
                  event.stopPropagation();
                  sendFeedFeedback('hide');
                }}
                style={({ pressed }) => [
                  styles.rankingActionButton,
                  pressed ? styles.rankingActionButtonPressed : null,
                ]}
              >
                <Text style={styles.rankingActionText}>Hide</Text>
              </Pressable>
            </View>
          ) : null}
        </View>
      ) : null}
      <View style={styles.engagementRow}>
        <View style={styles.engagementLeft}>
          <CompactReactionButton
            disabled={!user?.uid || reactionDisabled || hasPendingSync}
            loading={false}
            selectedReaction={visibleReaction}
            testID={`post-react-${post.id}`}
            onSelect={chooseReaction}
          />
          {totalVisibleReactions > 0 ? (
            <Text style={[styles.commentCountText, styles.reactionCountText]}>
              {totalVisibleReactions}
            </Text>
          ) : null}
          {showCommentCount ? (
            <Pressable
              accessibilityLabel="View comments"
              accessibilityRole="button"
              disabled={hasPendingSync || !showCommentAction}
              onPress={(event) => {
                event.stopPropagation();
                openCommentComposer();
              }}
              style={({ pressed }) => [
                styles.commentCountButton,
                pressed && !hasPendingSync && showCommentAction
                  ? styles.commentButtonPressed
                  : null,
              ]}
            >
              <MessageCircle
                color={colors.mutedText}
                size={22}
                strokeWidth={2.2}
              />
              <Text style={styles.commentCountText}>{post.commentCount}</Text>
            </Pressable>
          ) : null}
          {visibleVisibility === 'public' ? (
            <Pressable
              accessibilityLabel="Share post"
              accessibilityRole="button"
              disabled={!canOpenShareMenu}
              onPress={(event) => {
                event.stopPropagation();
                openShareMenu();
              }}
              style={({ pressed }) => [
                styles.commentCountButton,
                pressed && canOpenShareMenu
                  ? styles.commentButtonPressed
                  : null,
                !canOpenShareMenu ? styles.actionDisabled : null,
              ]}
            >
              <Share2 color={colors.mutedText} size={21} strokeWidth={2.2} />
              <Text style={styles.commentCountText}>{visibleShareCount}</Text>
            </Pressable>
          ) : null}
        </View>
        <View style={styles.engagementRight}>
          {totalVisibleReactions > 0 ? (
            <ReactionSummaryButton
              counts={visibleReactionCounts}
              currentUid={user?.uid}
              selectedReaction={visibleReaction}
              target={{ kind: 'post', postId: post.id }}
            />
          ) : null}
        </View>
      </View>
      <DeletePostModal
        deleting={deletePost.isPending || discardingFailedUpload}
        mode={canDiscardFailedPost ? 'failed_upload' : 'synced'}
        visible={confirmingDelete}
        onCancel={() => {
          if (!deletePost.isPending && !discardingFailedUpload) {
            setConfirmingDelete(false);
          }
        }}
        onConfirm={() => {
          void confirmDeletePost();
        }}
      />
      <EditContentModal
        allowEmpty={Boolean(post.media || post.linkCard || post.reshare)}
        body={post.body ?? ''}
        loading={editSaving}
        maxLength={MAX_POST_BODY_LENGTH}
        mentions={post.profileMentions ?? []}
        scope={{ kind: 'post' }}
        title="Edit post"
        visible={editingPost}
        onClose={() => {
          if (!editSaving) {
            setEditingPost(false);
          }
        }}
        onSave={(nextBody, nextMentions) => {
          void savePostEdit(nextBody, nextMentions);
        }}
      />
      <EditHistoryModal
        currentUid={user?.uid}
        loadHistory={() => loadPostEditHistory(post.id)}
        title="Post edit history"
        visible={showEditHistory}
        onClose={() => setShowEditHistory(false)}
      />
      <ShareOptionsModal
        busy={sharing}
        options={[
          ...(canResharePost
            ? [
                {
                  description:
                    'Create a new public post with the original embedded.',
                  kind: 'reshare' as const,
                  loading: sharingAction === 'reshare',
                  onPress: openReshareComposer,
                  title: 'Reshare on Frienzzi',
                },
              ]
            : []),
          {
            description: "Open Facebook with Frienzzi's share dialog.",
            kind: 'facebook',
            loading: sharingAction === 'facebook',
            onPress: () => {
              void sharePostToFacebook();
            },
            title: 'Share to Facebook',
          },
          {
            description: nativeShareDescription(),
            kind: 'native',
            loading: sharingAction === 'native',
            onPress: () => {
              void sharePostWithDevice();
            },
            title: nativeShareTitle(),
          },
          {
            description: 'Copy the post link so you can paste it anywhere.',
            kind: 'copy',
            loading: sharingAction === 'copy',
            onPress: () => {
              void copyPostLink();
            },
            title: 'Copy link',
          },
        ]}
        subtitle="Choose where this public Frienzzi post goes."
        title="Share post"
        visible={shareMenuVisible}
        onClose={() => {
          if (!sharing) {
            setShareMenuVisible(false);
          }
        }}
      />
      <ResharePostComposeModal
        body={reshareBody}
        currentUid={user?.uid}
        mentions={reshareMentions}
        originalPost={post}
        posting={sharingAction === 'reshare'}
        visible={reshareComposerVisible}
        onBodyChange={setReshareBody}
        onClose={() => {
          if (!sharing) {
            setReshareComposerVisible(false);
            setReshareBody('');
            setReshareMentions([]);
          }
        }}
        onMentionsChange={setReshareMentions}
        onSubmit={() => {
          void publishResharePostOnFrienzzi();
        }}
      />
      <Modal
        animationType="fade"
        transparent
        visible={showRankingInfo}
        onRequestClose={() => setShowRankingInfo(false)}
      >
        <Pressable
          style={styles.deleteModalBackdrop}
          onPress={() => setShowRankingInfo(false)}
        >
          <Pressable
            style={styles.deleteModal}
            onPress={(event) => event.stopPropagation()}
          >
            <Text style={styles.deleteModalTitle}>Why this post?</Text>
            <Text style={styles.deleteModalText}>
              Frienzzi ranks posts with deterministic signals like recency,
              follows, conversations, and your feedback. AI helps label content,
              but it does not choose the final order.
            </Text>
            {rankingReasonLabels.map((label) => (
              <View key={label} style={styles.rankingModalReason}>
                <Text style={styles.rankingModalReasonText}>{label}</Text>
              </View>
            ))}
            <View style={styles.deleteModalActions}>
              <Button
                title="Done"
                style={styles.deleteModalButton}
                onPress={() => setShowRankingInfo(false)}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );

  return (
    <View style={styles.card} testID={`post-card-${post.id}`}>
      {cardContent}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.lg,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
  },
  avatar: {
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 40,
  },
  avatarImage: {
    height: '100%',
    width: '100%',
  },
  avatarText: {
    color: colors.secondary,
    fontSize: 16,
    fontWeight: '900',
  },
  headerText: {
    flex: 1,
    gap: 0,
    minWidth: 0,
  },
  postHeaderActions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
  },
  headerIconButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 18,
    borderWidth: 1,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  headerIconButtonDisabled: {
    opacity: 0.5,
  },
  headerIconButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  deletePostButton: {
    alignItems: 'center',
    backgroundColor: '#fff4f7',
    borderColor: colors.border,
    borderRadius: 18,
    borderWidth: 1,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  deletePostButtonDisabled: {
    opacity: 0.5,
  },
  deletePostButtonPressed: {
    backgroundColor: '#ffe7ee',
  },
  authorButton: {
    flexShrink: 1,
    minWidth: 0,
  },
  authorRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
    minHeight: 20,
    minWidth: 0,
  },
  author: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '800',
    lineHeight: 20,
  },
  authorUsername: {
    color: colors.mutedText,
    flexShrink: 1,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 18,
    maxWidth: '52%',
  },
  time: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
  },
  timeButton: {
    borderRadius: 6,
    paddingVertical: 1,
  },
  timeButtonPressed: {
    opacity: 0.75,
  },
  timeControl: {
    position: 'relative',
    zIndex: 35,
  },
  timeRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 5,
    minHeight: 18,
    minWidth: 0,
  },
  timeSeparator: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 18,
  },
  editedButton: {
    alignItems: 'center',
    borderRadius: 999,
    flexDirection: 'row',
    gap: 3,
    minHeight: 22,
    paddingHorizontal: 2,
  },
  editedButtonPressed: {
    opacity: 0.78,
  },
  editedText: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
  },
  visibilityButton: {
    alignItems: 'center',
    borderRadius: 999,
    height: 22,
    justifyContent: 'center',
    width: 22,
  },
  visibilityButtonEditable: {
    backgroundColor: colors.surfaceTint,
  },
  visibilityButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  visibilityButtonSaving: {
    opacity: 0.55,
  },
  visibilityControl: {
    position: 'relative',
    zIndex: 20,
  },
  visibilityMenu: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: 2,
    minWidth: VISIBILITY_MENU_WIDTH,
    padding: 5,
    position: 'absolute',
    shadowColor: '#000000',
    shadowOffset: { height: 8, width: 0 },
    shadowOpacity: 0.14,
    shadowRadius: 18,
  },
  visibilityMenuOverlay: {
    backgroundColor: 'transparent',
    flex: 1,
  },
  visibilityMenuItem: {
    alignItems: 'center',
    borderRadius: 7,
    flexDirection: 'row',
    gap: spacing.sm,
    minHeight: 36,
    paddingHorizontal: spacing.sm,
  },
  visibilityMenuItemPressed: {
    backgroundColor: colors.surfacePressed,
  },
  visibilityMenuItemSelected: {
    backgroundColor: colors.surfaceTint,
  },
  visibilityMenuItemText: {
    color: colors.text,
    fontSize: 13,
    fontWeight: '800',
  },
  visibilityMenuItemTextSelected: {
    color: colors.primary,
  },
  visibilityTooltip: {
    backgroundColor: colors.text,
    borderRadius: 6,
    bottom: 26,
    left: -18,
    minWidth: 64,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
    position: 'absolute',
    zIndex: 40,
  },
  visibilityTooltipText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '800',
    textAlign: 'center',
  },
  syncStatus: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  body: {
    color: colors.text,
    fontSize: 16,
    lineHeight: 23,
  },
  bodyButton: {
    borderRadius: 8,
  },
  bodyButtonPressed: {
    opacity: 0.82,
  },
  bodyMention: {
    color: colors.primary,
    fontWeight: '800',
  },
  commentButtonPressed: {
    opacity: 0.82,
  },
  commentCountButton: {
    alignItems: 'center',
    borderRadius: 999,
    flexDirection: 'row',
    gap: spacing.xs,
    minHeight: 36,
    paddingHorizontal: spacing.xs,
  },
  commentCountText: {
    color: colors.mutedText,
    fontSize: 14,
    fontWeight: '800',
  },
  actionDisabled: {
    opacity: 0.45,
  },
  rankingActionButton: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
  },
  rankingActionButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  rankingActionText: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
  },
  rankingActions: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  rankingPanel: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    justifyContent: 'space-between',
  },
  rankingReasonButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.xs,
    minHeight: 30,
    maxWidth: '100%',
    paddingHorizontal: spacing.sm,
  },
  rankingReasonButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  rankingReasonText: {
    color: colors.secondary,
    flexShrink: 1,
    fontSize: 12,
    fontWeight: '900',
  },
  rankingModalReason: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    marginTop: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  rankingModalReasonText: {
    color: colors.secondary,
    fontSize: 13,
    fontWeight: '900',
  },
  visibilityBadge: {
    backgroundColor: colors.surfaceTint,
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
  },
  visibilityBadgeText: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
  },
  engagementRow: {
    alignItems: 'center',
    borderTopColor: colors.border,
    borderTopWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
    minHeight: 40,
    paddingTop: spacing.sm,
  },
  engagementLeft: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 0,
    gap: spacing.xs,
  },
  engagementRight: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    minHeight: 32,
  },
  reactionCountText: {
    marginLeft: -4,
  },
  image: {
    height: '100%',
    width: '100%',
  },
  imageFrame: {
    backgroundColor: colors.surfaceTint,
    borderRadius: 8,
    overflow: 'hidden',
    width: '100%',
  },
  carouselArrow: {
    alignItems: 'center',
    backgroundColor: 'rgba(13, 9, 43, 0.58)',
    borderRadius: 18,
    height: 36,
    justifyContent: 'center',
    marginTop: -18,
    position: 'absolute',
    top: '50%',
    width: 36,
  },
  carouselArrowLeft: {
    left: spacing.sm,
  },
  carouselArrowRight: {
    right: spacing.sm,
  },
  carouselCounter: {
    backgroundColor: 'rgba(13, 9, 43, 0.68)',
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
    position: 'absolute',
    right: spacing.sm,
    top: spacing.sm,
  },
  carouselCounterText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '900',
  },
  carouselFrame: {
    backgroundColor: colors.surfaceTint,
    borderRadius: 8,
    overflow: 'hidden',
    width: '100%',
  },
  carouselPhotoFrame: {
    backgroundColor: colors.surfaceTint,
    height: '100%',
    width: '100%',
  },
  carouselScroll: {
    height: '100%',
    width: '100%',
  },
  carouselSlide: {
    height: '100%',
  },
  linkCard: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    overflow: 'hidden',
  },
  linkCardPressed: {
    backgroundColor: colors.surfacePressed,
  },
  linkCardImageFrame: {
    aspectRatio: 1,
    backgroundColor: colors.surface,
    width: 96,
  },
  linkCardImage: {
    height: '100%',
    width: '100%',
  },
  linkCardPlaceholder: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  linkCardText: {
    flex: 1,
    gap: 3,
    paddingRight: spacing.md,
  },
  linkCardEyebrow: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  linkCardTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 20,
  },
  linkCardSubtitle: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '700',
  },
  resharePreview: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    overflow: 'hidden',
    padding: spacing.md,
  },
  resharePreviewPressed: {
    backgroundColor: colors.surfacePressed,
  },
  resharePreviewHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  resharePreviewAvatar: {
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: 16,
    height: 32,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 32,
  },
  resharePreviewAvatarText: {
    color: colors.secondary,
    fontSize: 13,
    fontWeight: '900',
  },
  resharePreviewHeaderText: {
    flex: 1,
    gap: 0,
    minWidth: 0,
  },
  resharePreviewAuthor: {
    color: colors.text,
    flexShrink: 1,
    fontSize: 14,
    fontWeight: '900',
    lineHeight: 18,
  },
  resharePreviewUsername: {
    color: colors.mutedText,
    flexShrink: 1,
    fontSize: 13,
    lineHeight: 17,
  },
  resharePreviewMetaRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 5,
    minHeight: 17,
    minWidth: 0,
  },
  resharePreviewTime: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 17,
  },
  resharePreviewTimeSeparator: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 17,
  },
  resharePreviewBody: {
    color: colors.text,
    fontSize: 14,
    lineHeight: 20,
  },
  reshareNestedNotice: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
  },
  resharePreviewUnavailable: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: 3,
    padding: spacing.md,
  },
  resharePreviewUnavailableTitle: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
  resharePreviewUnavailableText: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
  },
  video: {
    backgroundColor: colors.text,
    borderRadius: 8,
    height: '100%',
    width: '100%',
  },
  videoFrame: {
    backgroundColor: colors.text,
    borderRadius: 8,
    overflow: 'hidden',
    width: '100%',
  },
  videoPreviewImage: {
    height: '100%',
    width: '100%',
  },
  videoPreviewOverlay: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  videoPreviewFallback: {
    alignItems: 'center',
    backgroundColor: colors.text,
    flex: 1,
    gap: spacing.xs,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  videoPreviewTitle: {
    color: colors.surface,
    fontSize: 15,
    fontWeight: '900',
    textAlign: 'center',
  },
  videoPreviewText: {
    color: colors.surfaceTint,
    fontSize: 13,
    textAlign: 'center',
  },
  playOverlay: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.25)',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  deleteModalBackdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(26, 12, 62, 0.28)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  deleteModal: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    gap: spacing.md,
    maxWidth: 440,
    padding: spacing.xl,
    shadowColor: '#000000',
    shadowOffset: { height: 10, width: 0 },
    shadowOpacity: 0.16,
    shadowRadius: 24,
    width: '100%',
  },
  shareModal: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    gap: spacing.lg,
    maxWidth: 430,
    padding: spacing.lg,
    shadowColor: '#000000',
    shadowOffset: { height: 10, width: 0 },
    shadowOpacity: 0.16,
    shadowRadius: 24,
    width: '100%',
  },
  reshareComposeModal: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    gap: spacing.lg,
    maxHeight: '92%',
    maxWidth: 520,
    padding: spacing.lg,
    shadowColor: '#000000',
    shadowOffset: { height: 10, width: 0 },
    shadowOpacity: 0.16,
    shadowRadius: 24,
    width: '100%',
  },
  reshareComposeScroll: {
    flexGrow: 0,
  },
  reshareComposeScrollContent: {
    gap: spacing.md,
  },
  reshareMessageInput: {
    minHeight: 94,
  },
  counter: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
    marginTop: -spacing.sm,
    textAlign: 'right',
  },
  shareModalHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  shareModalTitleBlock: {
    flex: 1,
    gap: 3,
  },
  shareModalTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  shareModalSubtitle: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
  },
  shareModalCloseButton: {
    alignItems: 'center',
    borderRadius: 18,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  shareModalCloseButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  shareOptionList: {
    gap: spacing.xs,
  },
  shareOption: {
    alignItems: 'center',
    borderRadius: 8,
    flexDirection: 'row',
    gap: spacing.md,
    minHeight: 58,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  shareOptionDisabled: {
    opacity: 0.5,
  },
  shareOptionIcon: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 19,
    borderWidth: 1,
    height: 38,
    justifyContent: 'center',
    width: 38,
  },
  shareOptionPressed: {
    backgroundColor: colors.surfacePressed,
  },
  shareOptionText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  shareOptionTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  shareOptionDescription: {
    color: colors.mutedText,
    fontSize: 12,
    lineHeight: 17,
  },
  facebookShareMark: {
    color: '#1877f2',
    fontSize: 22,
    fontWeight: '900',
    lineHeight: 24,
  },
  deleteModalTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  deleteModalText: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  deleteModalActions: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  deleteModalButton: {
    flex: 1,
  },
});
